import React, { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { CurrencyInr, Heart, Clock, Warning, ChartLineUp, MegaphoneSimple, TrendUp, Receipt } from "@phosphor-icons/react";

function Stat({ label, value, Icon, color = "#4FAC39", hint, testid }) {
  return (
    <div className="seva-card !rounded-xl" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#56624F]">{label}</div>
          <div className="font-display font-bold text-2xl md:text-3xl mt-1.5">{value}</div>
          {hint && <div className="text-[11px] text-[#56624F] mt-1">{hint}</div>}
        </div>
        <div className="w-11 h-11 rounded-full grid place-items-center" style={{ background: color + "20", color }}>
          <Icon size={22} weight="fill" />
        </div>
      </div>
    </div>
  );
}

function MetaAdsPanel() {
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [utm, setUtm] = useState([]);
  const [preset, setPreset] = useState("30d");

  const load = () => {
    api.get("/admin/meta-ads/stats", { params: { preset } }).then((r) => setStats(r.data)).catch(() => setStats(null));
    api.get("/admin/meta-ads/campaigns", { params: { preset } }).then((r) => setCampaigns(r.data?.items || [])).catch(() => setCampaigns([]));
    api.get("/admin/reports/utm-roas", { params: { preset } }).then((r) => setUtm(r.data || [])).catch(() => setUtm([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [preset]);

  if (!stats) {
    return (
      <div className="seva-card">
        <h2 className="font-display font-bold text-xl flex items-center gap-2"><MegaphoneSimple size={22} weight="fill" className="text-[#4FAC39]" /> Meta Ads ROAS &amp; Profit</h2>
        <p className="text-sm text-[#56624F] mt-2">Loading...</p>
      </div>
    );
  }

  const PRESETS = [
    { k: "today", l: "Today" },
    { k: "yesterday", l: "Yesterday" },
    { k: "7d", l: "7 days" },
    { k: "14d", l: "14 days" },
    { k: "30d", l: "30 days" },
    { k: "60d", l: "60 days" },
    { k: "90d", l: "90 days" },
  ];

  return (
    <div className="seva-card space-y-5" data-testid="meta-ads-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display font-bold text-xl flex items-center gap-2"><MegaphoneSimple size={22} weight="fill" className="text-[#4FAC39]" /> Meta Ads ROAS &amp; Profit</h2>
        <div className="inline-flex rounded-full border border-[#DCE5D4] p-1 text-xs flex-wrap" data-testid="meta-ads-preset">
          {PRESETS.map((p) => (
            <button key={p.k} onClick={() => setPreset(p.k)} className={"px-3 py-1.5 rounded-full font-semibold " + (preset === p.k ? "bg-[#4FAC39] text-white" : "text-[#56624F]")}>{p.l}</button>
          ))}
        </div>
      </div>

      {!stats.configured ? (
        <div className="text-sm bg-[#FFF8EC] border border-[#E4C87A] rounded-xl p-3 text-[#7A5E00]">Add <strong>Meta Ad Account ID</strong> and <strong>Marketing API token</strong> in Settings &rsaquo; Meta to see live ad performance.</div>
      ) : stats.error ? (
        <div className="text-sm bg-[#FDEAE5] border border-[#E4A8A0] rounded-xl p-3 text-[#7A3428]">Meta API error: {stats.error}</div>
      ) : null}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Ad Spend" value={formatINR(stats.ad_spend)} Icon={CurrencyInr} color="#B84A34" hint={`+${stats.gst_percent}% GST = ${formatINR(stats.total_spend_with_gst)}`} testid="stat-ad-spend" />
        <Stat label="Revenue (donations)" value={formatINR(stats.revenue)} Icon={CurrencyInr} color="#4FAC39" hint={`${stats.donations} donations · ${stats.since} to ${stats.until}`} testid="stat-revenue" />
        <Stat label="ROAS" value={stats.roas_including_gst.toFixed(2) + "x"} Icon={TrendUp} color="#1F6FB8" hint={`Gross ROAS: ${stats.roas.toFixed(2)}x`} testid="stat-roas" />
        <Stat label="Profit" value={formatINR(stats.profit)} Icon={ChartLineUp} color={stats.profit >= 0 ? "#2F7A4A" : "#B84A34"} hint="Revenue − (Ad Spend + GST)" testid="stat-profit" />
      </div>

      {!!campaigns.length && (
        <div>
          <div className="text-sm font-semibold mb-2">Meta Campaigns {campaigns.find(c => c.is_included === false) && <span className="text-xs text-[#56624F] font-normal">(greyed = excluded from totals)</span>}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#56624F] border-b border-[#DCE5D4]">
                  <th className="py-2 pr-4">Campaign</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4 text-right">Spend</th>
                  <th className="py-2 pr-4 text-right">Purchase Value</th>
                  <th className="py-2 text-right">ROAS</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id} className={"border-b border-[#DCE5D4]/50 " + (c.is_included === false ? "opacity-40" : "")}>
                    <td className="py-2 pr-4 font-medium truncate max-w-xs">{c.name}</td>
                    <td className="py-2 pr-4"><span className="text-xs px-2 py-0.5 rounded-full bg-[#EEF4E9] text-[#2F7A4A]">{c.status}</span></td>
                    <td className="py-2 pr-4 text-right">{formatINR(c.spend)}</td>
                    <td className="py-2 pr-4 text-right">{formatINR(c.purchase_value)}</td>
                    <td className="py-2 text-right font-semibold">{c.roas.toFixed(2)}x</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* UTM ROAS */}
      <div>
        <div className="text-sm font-semibold mb-2">ROAS by UTM Campaign</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#56624F] border-b border-[#DCE5D4]">
                <th className="py-2 pr-4">Source</th>
                <th className="py-2 pr-4">Campaign</th>
                <th className="py-2 pr-4 text-right">Donations</th>
                <th className="py-2 pr-4 text-right">Revenue</th>
                <th className="py-2 pr-4 text-right">Ad Spend (matched)</th>
                <th className="py-2 text-right">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {utm.map((u, i) => (
                <tr key={i} className="border-b border-[#DCE5D4]/50" data-testid={`utm-roas-row-${i}`}>
                  <td className="py-2 pr-4 font-medium">{u.source}</td>
                  <td className="py-2 pr-4 truncate max-w-xs">{u.campaign}</td>
                  <td className="py-2 pr-4 text-right">{u.donations}</td>
                  <td className="py-2 pr-4 text-right font-semibold">{formatINR(u.revenue)}</td>
                  <td className="py-2 pr-4 text-right text-[#B84A34]">{u.ad_spend > 0 ? formatINR(u.ad_spend) : "—"}</td>
                  <td className="py-2 text-right font-semibold">{u.ad_spend > 0 ? u.roas.toFixed(2) + "x" : "—"}</td>
                </tr>
              ))}
              {!utm.length && <tr><td colSpan={6} className="py-6 text-center text-[#56624F]">No UTM-tagged donations in this window.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="text-[11px] text-[#56624F] mt-2">Ad spend is matched by UTM campaign name == Meta ad campaign name (case-insensitive). Name them identically for attribution.</div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/donations/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div className="text-[#56624F]">Loading...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display font-bold text-3xl md:text-4xl">Dashboard</h1>
        <p className="text-[#56624F] mt-1">Overview of donations, ads performance &amp; UTM</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total Raised" value={formatINR(stats.total_raised)} Icon={CurrencyInr} testid="stat-total-raised" />
        <Stat label="Donations" value={stats.total_donations} Icon={Heart} color="#2F7A4A" testid="stat-donations" />
        <Stat label="Pending" value={stats.pending} Icon={Clock} color="#B88A2F" testid="stat-pending" />
        <Stat label="Failed" value={stats.failed} Icon={Warning} color="#B84A34" testid="stat-failed" />
      </div>

      <MetaAdsPanel />

      <div className="seva-card">
        <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Receipt size={20} weight="fill" className="text-[#4FAC39]" /> Donations (last 30 days)</h2>
        <div style={{ height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={stats.daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DCE5D4" />
              <XAxis dataKey="date" stroke="#56624F" fontSize={11} />
              <YAxis stroke="#56624F" fontSize={11} />
              <Tooltip />
              <Bar dataKey="total" fill="#4FAC39" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="seva-card">
        <h2 className="font-display font-bold text-lg mb-4">UTM Breakdown (top 10)</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#56624F] border-b border-[#DCE5D4]">
                <th className="py-2 pr-4">Source</th>
                <th className="py-2 pr-4">Medium</th>
                <th className="py-2 pr-4">Campaign</th>
                <th className="py-2 pr-4 text-right">Donations</th>
                <th className="py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {stats.utm_breakdown.map((u, i) => (
                <tr key={i} className="border-b border-[#DCE5D4]/50">
                  <td className="py-2 pr-4 font-medium">{u.source}</td>
                  <td className="py-2 pr-4">{u.medium}</td>
                  <td className="py-2 pr-4">{u.campaign}</td>
                  <td className="py-2 pr-4 text-right">{u.count}</td>
                  <td className="py-2 text-right font-semibold">{formatINR(u.total)}</td>
                </tr>
              ))}
              {!stats.utm_breakdown.length && (
                <tr><td colSpan={5} className="py-6 text-center text-[#56624F]">No UTM data yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
